import React, {useCallback, useEffect} from 'react';
import {useAppDispatch, store, useAppSelector} from 'store/customer';
import {
    duplicateIdDelete,
    setDeletedId,
    setIsRoomPlannerEditing,
    setIsDeleting,
    setIsAutoSaving,
    updateCabinetPosition,
    getRoomPlannerRoomIndex,
    setIsBusy,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {dispatchCabinetDeleteToHistory} from 'components/customer/RoomPlanner/helpers/history/cabinetDeleteAction';
import {formatHistoryTitlePrefix} from 'components/customer/RoomPlanner/helpers/history/formatHistoryTitle';
import {roomPlannerApi} from 'components/customer/RoomPlanner/store/roomPlannerApi';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';
import {
    invalidateParticularRoomAndCost,
    useDeleteCabinetMutation,
    useSaveProductMutation,
    useUpdateProductQuantityMutation,
} from 'components/customer/Product/store/productApi';
import {useDialogContext} from 'contexts/DialogContext';
import {useJobContext} from 'contexts/JobContext';
import FloatingMenuButton, {
    FloatingMenuButtonIcon,
} from 'components/customer/RoomPlanner/components/common/FloatingMenuButton';
import {PartialJob} from 'shared/types/PartialJob';
import {invalidateTotalProductCount} from 'components/customer/Job/store/jobApi';
import {deleteCabinetMesh} from 'components/customer/RoomPlanner/helpers/deleteCabinetMesh';
import {useSearchParams} from 'react-router-dom';
import {dispatchClearChanges} from 'store/customer/appSlice';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import * as THREE from 'three';
import useProductValuesMapping from 'components/customer/RoomPlanner/helpers/useProductValuesMapping';
import {normalizeRawProductValues} from 'components/customer/RoomPlanner/helpers/normalizeRawProductValues';
import {applyDoorDefaultsToProduct} from 'hooks/Product/helpers/roomPlannerProducts';
import {appendProductTo3dPreview} from 'components/customer/RoomPlanner/helpers/appendProductTo3dPreview';
import type {Product} from 'components/customer/Product/entity/Product';
import type {
    UndoActionContext,
    CabinetMesh,
    RoomPlannerProduct,
    RoomPlannerProductWithTemplate,
} from 'components/customer/RoomPlanner/types';
import {
    useIsMobileLandscape,
    useIsPortraitDevices,
} from 'shared/helpers/DeviceSize';

const getStoredPose = (cabinet: THREE.Object3D) => {
    const rotation = cabinet.rotation;
    return {
        position: {
            x: cabinet.position.x,
            y: cabinet.position.y,
            z: cabinet.position.z,
        },
        rotation: {
            x: rotation.x,
            y: rotation.y,
            z: rotation.z,
            order: rotation.order ?? 'XYZ',
        },
    };
};

const normalizeShelvesToFlatArrays = (
    values: Record<string, unknown>
): void => {
    const shelvesArray = values.hasOwnProperty('shelves') && values.shelves;
    const isShelvesArrayOfObjects =
        Array.isArray(shelvesArray) &&
        shelvesArray.length > 0 &&
        typeof shelvesArray[0] === 'object' &&
        shelvesArray[0] !== null &&
        ('shelf_type' in (shelvesArray[0] as object) ||
            'shelf_style' in (shelvesArray[0] as object) ||
            'shelf_position' in (shelvesArray[0] as object) ||
            'shelf_offset' in (shelvesArray[0] as object));

    if (!isShelvesArrayOfObjects) return;

    const shelfFields = [
        'shelf_type',
        'shelf_style',
        'shelf_position',
        'shelf_offset',
    ];
    const simpleShelfStyle =
        values.hasOwnProperty('cabinet_simple_shelves') &&
        values.cabinet_simple_shelves &&
        values.hasOwnProperty('simple_shelf_type')
            ? Number(values.simple_shelf_type)
            : undefined;

    shelfFields.forEach((field) => {
        (values as Record<string, unknown[]>)[String(field)] = [];
        for (let i = 0; i < (shelvesArray as unknown[]).length; i++) {
            const shelf = (shelvesArray as Record<string, unknown>[])[
                Number(i)
            ];
            if (field.indexOf('position') > -1) {
                (values as Record<string, unknown[]>)[String(field)].push(
                    shelf.hasOwnProperty(field) ? shelf[String(field)] : 0.5
                );
            } else if (
                field.indexOf('shelf_style') > -1 &&
                typeof simpleShelfStyle === 'number'
            ) {
                (values as Record<string, unknown[]>)[String(field)].push(
                    simpleShelfStyle
                );
            } else {
                const val = shelf.hasOwnProperty(field)
                    ? shelf[String(field)]
                    : 0;
                (values as Record<string, unknown[]>)[String(field)].push(
                    typeof val === 'number' ? val : Number(val) || 0
                );
            }
        }
    });
    values.shelves = [];
};

const Delete = () => {
    const {getMappedValues} = useProductValuesMapping();
    const roomIndex = useAppSelector(getRoomPlannerRoomIndex);
    const [deleteProduct] = useDeleteCabinetMutation();
    const [saveProduct] = useSaveProductMutation();
    const dispatch = useAppDispatch();
    const [updateQuantity] = useUpdateProductQuantityMutation();
    const {room, updateTotalProductCountManual} = useJobContext() as PartialJob;
    const {showDialog} = useDialogContext();

    const isMobileLandscape = useIsMobileLandscape();
    const isPortraitDevices = useIsPortraitDevices();

    const [searchParams, setSearchParams] = useSearchParams();
    const {
        selectedCabinetGroup,
        setSelectedCabinetGroup,
        cabinetGroupListRef,
        setDeleteHandler,
    } = useRoomPlannerContext();

    const deselectEditingCabinet = useCallback(
        (id: number, type: number) => {
            const params = new URLSearchParams(searchParams);
            let paramsChanged = false;

            if (
                params.has('cabinetId') &&
                params.get('cabinetId') === id.toString()
            ) {
                params.delete('cabinetId');
                paramsChanged = true;
            }

            if (
                params.has('product') &&
                params.get('product') === type.toString()
            ) {
                params.delete('product');
                paramsChanged = true;
            }

            if (paramsChanged) {
                setSearchParams(params);
                dispatch(setIsRoomPlannerEditing(false));
            }
        },
        [searchParams]
    );

    const invalidateCaches = useCallback(() => {
        dispatch(invalidateTotalProductCount());
        dispatch(invalidateParticularRoomAndCost(Number(room.id)));

        dispatchClearChanges();
        setSelectedCabinetGroup?.(null);
    }, [dispatch, room.id, setSelectedCabinetGroup]);

    const recordDeleteInHistory = useCallback(
        (
            cabinetGroup: CabinetMesh,
            cabinetId: number,
            userData: {values: RoomPlannerProduct}
        ) => {
            const storedPose = getStoredPose(cabinetGroup);

            const undoAction = async (ctx?: UndoActionContext) => {
                const mappedValues = getMappedValues(cabinetGroup);
                if (!mappedValues) return;

                mappedValues.job_cabinet_id = undefined;
                mappedValues.shelves =
                    cabinetGroup?.userData?.values?.shelves ??
                    mappedValues.shelves;
                delete mappedValues.template_3d;

                applyDoorDefaultsToProduct(
                    mappedValues as unknown as Parameters<
                        typeof applyDoorDefaultsToProduct
                    >[0]
                );

                normalizeRawProductValues(
                    mappedValues as unknown as Record<string, unknown>
                );

                normalizeShelvesToFlatArrays(
                    mappedValues as unknown as Record<string, unknown>
                );

                try {
                    const response = await saveProduct({
                        product: mappedValues as unknown as Product,
                    }).unwrap();

                    (mappedValues as {job_cabinet_id?: number}).job_cabinet_id =
                        response.id;

                    const restoredCabinetRef = {
                        current: null as THREE.Group | null,
                    };

                    await appendProductTo3dPreview(
                        mappedValues as unknown as Parameters<
                            typeof appendProductTo3dPreview
                        >[0],
                        Number(userData.values.type),
                        room,
                        cabinetGroupListRef,
                        false,
                        false,
                        false,
                        (cabinets) => {
                            const newCabinet = cabinets[0];
                            if (!newCabinet) return;

                            const values = newCabinet.userData
                                .values as unknown as Record<string, unknown>;
                            values.id = response.id;
                            values.job_cabinet_id = response.id;

                            newCabinet.position.set(
                                storedPose.position.x,
                                storedPose.position.y,
                                storedPose.position.z
                            );
                            newCabinet.rotation.set(
                                storedPose.rotation.x,
                                storedPose.rotation.y,
                                storedPose.rotation.z,
                                storedPose.rotation.order
                            );
                            newCabinet.updateMatrixWorld();
                            restoredCabinetRef.current = newCabinet;
                        }
                    );

                    const cabinet = restoredCabinetRef.current;
                    if (cabinet) {
                        dispatch(
                            updateCabinetPosition({
                                position: cabinet.position,
                                rotation: cabinet.rotation,
                                id: response.id,
                                index: 0,
                                skipHistory: true,
                            })
                        );
                        const layoutId = store.getState().roomPlanner.layoutId;
                        if (layoutId != null) {
                            await store
                                .dispatch(
                                    roomPlannerApi.endpoints.saveCabinetPosition.initiate(
                                        {
                                            jobCabinetId: response.id,
                                            layout_room_id: layoutId,
                                            position: cabinet.position,
                                            rotation: cabinet.rotation,
                                            index: 0,
                                        }
                                    )
                                )
                                .unwrap();
                        }

                        dispatch(setIsAutoSaving(false));
                    }

                    ctx?.setRestoredCabinetId(response.id);
                    updateTotalProductCountManual(false, false, -1);
                    invalidateCaches();
                } finally {
                    dispatch(setIsAutoSaving(false));
                    dispatch(setIsBusy(false));
                }
            };

            dispatchCabinetDeleteToHistory(dispatch, {
                title: `**Delete** ${formatHistoryTitlePrefix(roomIndex, userData.values.room_cab_number)} ${userData.values.type_name ?? ''}`.trimEnd(),
                data: {
                    id: cabinetId,
                    values: userData.values,
                    undoAction,
                },
            });
        },
        [
            getMappedValues,
            saveProduct,
            room,
            cabinetGroupListRef,
            dispatch,
            updateTotalProductCountManual,
            invalidateCaches,
        ]
    );

    const handleDeleteClick = useCallback(() => {
        const cabinetGroupToDeleteValue = selectedCabinetGroup;

        const rawUserData = cabinetGroupToDeleteValue?.userData;

        const isValidUserData = (data: unknown) => {
            if (
                typeof data === 'object' &&
                data !== null &&
                'values' in data &&
                'deleteCabinet' in data
            ) {
                const maybeUserData = data as Record<string, unknown>;
                return (
                    typeof maybeUserData.deleteCabinet === 'function' &&
                    (maybeUserData.values === undefined ||
                        (typeof maybeUserData.values === 'object' &&
                            maybeUserData.values !== null &&
                            'id' in maybeUserData.values))
                );
            }
            return false;
        };

        if (isValidUserData(rawUserData)) {
            const userData = rawUserData;

            const cabinetId = Number(userData.values?.id);
            const product = Number(userData.values.type);
            const cabinetQuantity = (
                userData.values as RoomPlannerProductWithTemplate
            ).cabinet_quantity;

            if (cabinetId) {
                if (cabinetQuantity > 1) {
                    showDialog({
                        title: 'Delete Item',
                        message:
                            'There are multiple items of this product. Would you like to delete one or all of them?',
                        hideYesButton: true,
                        hideNoButton: true,
                        buttons: [
                            {
                                name: 'Delete One',
                                show: true,
                                action: async () => {
                                    dispatch(setIsDeleting(true));
                                    dispatch(setIsAutoSaving(true));
                                    try {
                                        deselectEditingCabinet(
                                            cabinetId,
                                            product
                                        );

                                        const [, id] =
                                            userData.values.duplicate_id.split(
                                                '-'
                                            );

                                        if (!isNaN(Number(id))) {
                                            dispatch(
                                                duplicateIdDelete(
                                                    cabinetId,
                                                    Number(id)
                                                )
                                            );
                                        }

                                        await updateQuantity({
                                            cabinetId,
                                            quantity: cabinetQuantity - 1,
                                            index: Number(id),
                                        });

                                        if (cabinetGroupListRef.current) {
                                            cabinetGroupListRef.current.forEach(
                                                (cabinet) => {
                                                    if (
                                                        cabinet.userData.values
                                                            .id == cabinetId &&
                                                        cabinet.uuid !=
                                                            cabinetGroupToDeleteValue.uuid
                                                    ) {
                                                        cabinet.userData.values =
                                                            {
                                                                ...cabinet
                                                                    .userData
                                                                    .values,
                                                                cabinet_quantity:
                                                                    cabinetQuantity -
                                                                    1,
                                                            };
                                                    }
                                                }
                                            );
                                        }

                                        deleteCabinetMesh(
                                            cabinetGroupToDeleteValue,
                                            false,
                                            cabinetGroupListRef
                                        );
                                        updateTotalProductCountManual(
                                            false,
                                            false,
                                            1
                                        );
                                        invalidateCaches();
                                    } finally {
                                        dispatch(setIsDeleting(false));
                                        dispatch(setIsAutoSaving(false));
                                    }
                                },
                            },
                            {
                                name: 'Delete all',
                                show: true,
                                variant: 'danger',
                                action: async () => {
                                    dispatch(setIsDeleting(true));
                                    dispatch(setIsAutoSaving(true));
                                    try {
                                        deselectEditingCabinet(
                                            cabinetId,
                                            product
                                        );
                                        await deleteProduct({cabinetId});
                                        deleteCabinetMesh(
                                            cabinetGroupToDeleteValue,
                                            true,
                                            cabinetGroupListRef
                                        );

                                        updateTotalProductCountManual(
                                            false,
                                            false,
                                            cabinetGroupToDeleteValue.userData
                                                .values.cabinet_quantity
                                        );

                                        invalidateCaches();
                                    } finally {
                                        dispatch(setIsDeleting(false));
                                        dispatch(setIsAutoSaving(false));
                                    }
                                },
                            },
                            {
                                name: 'Cancel',
                                show: true,
                                variant: 'warning',
                            },
                        ],
                    });
                } else {
                    dispatch(setIsDeleting(true));
                    dispatch(setIsAutoSaving(true));
                    deselectEditingCabinet(cabinetId, product);

                    deleteProduct({cabinetId})
                        .then(() => {
                            dispatch(setDeletedId(cabinetId));
                            deleteCabinetMesh(
                                cabinetGroupToDeleteValue,
                                false,
                                cabinetGroupListRef
                            );
                            updateTotalProductCountManual(false, false, 1);
                            invalidateCaches();
                        })
                        .catch(() => {
                            invalidateCaches();
                        })
                        .finally(() => {
                            dispatch(setIsDeleting(false));
                            dispatch(setIsAutoSaving(false));
                        });

                    recordDeleteInHistory(
                        cabinetGroupToDeleteValue,
                        cabinetId,
                        userData
                    );
                }
            }
        }
    }, [
        selectedCabinetGroup,
        deleteProduct,
        dispatch,
        invalidateCaches,
        deselectEditingCabinet,
        updateQuantity,
        cabinetGroupListRef,
        showDialog,
        updateTotalProductCountManual,
        searchParams,
        setSearchParams,
        recordDeleteInHistory,
    ]);

    // Register the delete handler with the context for keyboard shortcut
    useEffect(() => {
        if (setDeleteHandler) {
            setDeleteHandler(handleDeleteClick);
        }
        return () => setDeleteHandler?.(null);
    }, [handleDeleteClick, setDeleteHandler]);

    return (
        <OverlayTrigger
            placement="top"
            overlay={<strong>Delete (Delete or Backspace)</strong>}>
            <FloatingMenuButton onClick={handleDeleteClick}>
                <FloatingMenuButtonIcon
                    className="icon_button"
                    color={
                        isMobileLandscape || isPortraitDevices
                            ? undefined
                            : '#b2bec3'
                    }
                    iconName="Layout-Delete.svg"
                />
                <label>Delete</label>
            </FloatingMenuButton>
        </OverlayTrigger>
    );
};

export default Delete;
